import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_CONFIG, STORAGE_KEYS, SECURITY_HEADERS } from '../constants/api';
import { authApiService } from './auth-api';

// ----------------------------------------------------------------------
// BASE API CLIENT
// ----------------------------------------------------------------------

class ApiClient {
  private instance: AxiosInstance;

  private isRefreshing = false;

  private refreshQueue: Array<(token: string) => void> = [];

  constructor() {
    this.instance = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: API_CONFIG.HEADERS,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem(STORAGE_KEYS.AUTHENTICATE_TOKEN);
        if (token) {
          config.headers[SECURITY_HEADERS.AUTHENTICATE_TOKEN] = token;
        }

        // const funcsTagActive = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
        // if (funcsTagActive) {
        //   config.headers[SECURITY_HEADERS.FUNCS_TAG_ACTIVE] = funcsTagActive;
        // }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Handle 429 Too Many Requests
        if (error.response?.status === 62) {
          throw new Error('Bạn đã gửi quá nhiều yêu cầu. Vui lòng đợi một lát và thử lại.');
        }
 
        // Handle 401 errors
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // Wait for refresh to complete
            return new Promise((resolve) => {
              this.refreshQueue.push((token: string) => {
                originalRequest.headers[SECURITY_HEADERS.AUTHENTICATE_TOKEN] = token;
                resolve(this.instance(originalRequest));
              });
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const refreshResponse = await authApiService.refreshToken();
            const newToken = refreshResponse.oneItem?.authenticateToken;

            if (newToken) {
              // Retry all queued requests
              this.refreshQueue.forEach((callback) => callback(newToken));
              this.refreshQueue = [];
              
              originalRequest.headers[SECURITY_HEADERS.AUTHENTICATE_TOKEN] = newToken;
              return await this.instance(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, clear tokens and redirect
            this.clearAuthTokens();
            window.location.href = '/auth/jwt/login';
            return await Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // eslint-disable-next-line class-methods-use-this
  public getAuthHeaders(controller?: string, isMultipart = false): Record<string, string> {
    const token = localStorage.getItem(STORAGE_KEYS.AUTHENTICATE_TOKEN);
    const permissionsStr = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
    
    let funcsTagActive = '';
    if (permissionsStr && controller) {
      try {
        const permissions = JSON.parse(permissionsStr);
        const controllerPermission = permissions.find(
          (perm: any) => perm.tenController?.trim().toLowerCase() === controller.trim().toLowerCase()
        );
        if (controllerPermission?.funcsTagActive) {
          funcsTagActive = controllerPermission.funcsTagActive;
        }
      } catch (e) {
        console.error('Error parsing permissions:', e);
      }
    }

    const headers: Record<string, string> = {
      'Content-Type': isMultipart ? 'multipart/form-data' : 'application/json',
    };

    if (token) headers.AuthenticateToken = token;
    if (funcsTagActive) headers.funcsTagActive = funcsTagActive;

    return headers;
  }

  // Generic request methods
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(url, config);
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.post<T>(url, data, config);
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.put<T>(url, data, config);
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.delete<T>(url, config);
  }

  // eslint-disable-next-line class-methods-use-this
  private clearAuthTokens(): void {
    localStorage.removeItem(STORAGE_KEYS.AUTHENTICATE_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.EXPIRED_AUTHENTICATE_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.EXPIRED_REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.SERVICE_URL);
    localStorage.removeItem(STORAGE_KEYS.USER_INFO);
    localStorage.removeItem(STORAGE_KEYS.PERMISSIONS);
    localStorage.removeItem(STORAGE_KEYS.MENU_ITEMS);
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// ----------------------------------------------------------------------
// ERROR HANDLING UTILITIES
// ----------------------------------------------------------------------

export function handleApiError(error: any, defaultMessage: string): never {
  if (error.response?.data) {
    throw error.response.data; 
  }

  if (error.response?.status === 429) {
    throw new Error(error.response.message);
  }

  if (error.code === 'ECONNABORTED') {
    throw new Error('Hết thời gian chờ. Vui lòng thử lại.');
  }

  if (error.message === 'Network Error') {
    throw new Error('Máy chủ tạm thời không hoạt động');
  }

  throw new Error(error.message || defaultMessage);
}

// ----------------------------------------------------------------------
// COMMON API OPERATIONS
// ----------------------------------------------------------------------

export async function apiGet<T>(
  endpoint: string,
  controller?: string,
  errorMessage = 'Lấy dữ liệu thất bại'
): Promise<T> {
  try {
    const headers = apiClient.getAuthHeaders(controller);
    const response = await apiClient.get<T>(endpoint, { 
      headers, 
      timeout: 30000 
    });
    
    return response.data;
  } catch (error: any) {
    return handleApiError(error, errorMessage);  
  }
}

export async function apiPost<T>(
  endpoint: string,
  data: any,
  controller?: string,
  isMultipart = false,
  errorMessage = 'Thao tác thất bại'
): Promise<T> {
  try {
    const headers = apiClient.getAuthHeaders(controller, isMultipart);
    const response = await apiClient.post<T>(endpoint, data, { 
      headers, 
      timeout: isMultipart ? 60000 : 30000 
    });
    return response.data;
  } catch (error: any) {
    return handleApiError(error, errorMessage);
  }
}

export async function apiDelete<T>(
  endpoint: string,
  controller?: string,
  errorMessage = 'Xóa thất bại'
): Promise<T> {
  try {
    const headers = apiClient.getAuthHeaders(controller);
    const response = await apiClient.delete<T>(endpoint, { 
      headers, 
      timeout: 30000 
    });
    return response.data;
  } catch (error: any) {
    return handleApiError(error, errorMessage);
  }
}

// ----------------------------------------------------------------------
// QUERY STRING BUILDER
// ----------------------------------------------------------------------

export function buildQueryString(params?: Record<string, any>): string {
  if (!params) return '';

  const queryParams: string[] = [];

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        queryParams.push(`${key}=${encodeURIComponent(item)}`);
      });
    } else {
      queryParams.push(`${key}=${encodeURIComponent(value)}`);
    }
  });

  return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
}